import type { DetailedHTMLProps, InputHTMLAttributes, PropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { Icon } from '../../atoms/icon';

const Label = styled.label`
  ${({ theme }) => theme.typography.paragraph.medium.normal}
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  &:focus-within {
    outline: 1px dashed ${({ theme }) => theme.palette.text.onSurface.subtle};
  }
`;

const RadioError = styled.div`
  ${({ theme }) => theme.typography.paragraph.small.normal}
  color: ${({ theme }) => theme.palette.accent.secondary.strong};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RadioIcon = styled.div<{ checked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 2px solid;
  border-color: ${({ theme }) => theme.palette.text.onSurface.strong};
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing(2)};
  &::after {
    content: '';
    width: 14px;
    height: 14px;
    border-radius: 50%;
    visibility: hidden;
  }
`;

const StyledInput = styled.input`
  border: 0;
  padding: 0;
  margin: 0;
  position: absolute;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  &:checked + ${RadioIcon} {
    border-color: ${({ theme }) => theme.palette.focus.strong};
    &::after {
      visibility: visible;
      background: ${({ theme }) => theme.palette.focus.strong};
    }
  }
`;

export type RadioProps = PropsWithoutRef<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>> & {
  label?: string;
  error?: string;
  className?: string;
  children?: ReactNode;
};

export const Radio = forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  const { label, error, className, children, ...other } = props;

  return (
    <div className={className}>
      <Label data-testid={`radio-label-${props.name}-${props.value}`}>
        <StyledInput type="radio" aria-label={props.label} ref={ref} {...other} />
        <RadioIcon checked={props.checked} />
        {children || label}
      </Label>
      {error && (
        <RadioError>
          <Icon name="ErrorOutline" color="red" />
          {error}
        </RadioError>
      )}
    </div>
  );
});

Radio.displayName = 'Radio';
